import React, { useCallback, useLayoutEffect } from 'react';
import Button from '../../../../components/button';
import Icon from '../../../../components/_base/Icon';
import './GeneralSessions.scss';

export interface IPagination {
  firstIndex: number,
  lastIndex: number,
  step: number
}
interface IProps extends IPagination {
  setPaginationConfig: (pagination: IPagination) => void,
  trackLength: number,
  pageContentBlock: HTMLDivElement | null,
  itemWidth: number
}
const pageValues = {
  indexDecrease: 1,
  zeroIndex: 0,
  minSize: 1,
  maxSize: 6,
  itemPaddingWidth: 16,
  reservedWidth: 220
};

const SessionsPagination: React.FC<IProps> = (props: IProps) => {
  const { trackLength, setPaginationConfig, pageContentBlock, step, itemWidth } = props;

  const changePaginationStep = useCallback(() => {
    if (!pageContentBlock) return;
    const width = pageContentBlock.offsetWidth;
    let newStep = Math.floor((width - pageValues.reservedWidth) / (itemWidth + pageValues.itemPaddingWidth));
    newStep = Math.max(newStep, pageValues.minSize);
    newStep = Math.min(newStep, pageValues.maxSize);
    if (newStep !== step) {
      setPaginationConfig({ firstIndex: 0, lastIndex: newStep - pageValues.indexDecrease, step: newStep });
    }
  }, [pageContentBlock, setPaginationConfig, itemWidth, step]);

  useLayoutEffect(() => {
    const lastIndex = Math.min(trackLength - pageValues.indexDecrease, step - pageValues.indexDecrease );
    setPaginationConfig({ firstIndex: pageValues.zeroIndex, step: step, lastIndex });
  }, [trackLength, setPaginationConfig, step]);

  useLayoutEffect(() => {
    changePaginationStep();
    window.addEventListener('resize', changePaginationStep);
    return () => window.removeEventListener('resize', changePaginationStep);
  }, [pageContentBlock, changePaginationStep]);

  const increasePages = () => {
    let firstIndex = Math.min(trackLength - pageValues.indexDecrease, props.lastIndex + step);
    firstIndex = Math.max(pageValues.zeroIndex, firstIndex - step + pageValues.indexDecrease);
    const lastIndex = Math.min(trackLength - pageValues.indexDecrease, props.lastIndex + step);
    setPaginationConfig({ firstIndex, lastIndex, step: step });
  };
  const decreasePages = () => {
    const firstIndex = Math.max(pageValues.zeroIndex, props.firstIndex - step);
    const lastIndex = Math.max(pageValues.zeroIndex, props.firstIndex - step) + step - pageValues.indexDecrease;
    setPaginationConfig({ firstIndex, lastIndex, step: step });
  };

  if (!pageContentBlock || !step || trackLength < step + pageValues.indexDecrease) return null;
  return (
    <div className='general-sessions-arrow-buttons w-45px text-center text-primary flex py-2 justify-around pagination-btn'>
      <Button.Common 
        name='Go to the previous page'
        className={'flex items-center rounded without-common-hover on-hover-shadow outline-auto-visible ' + (props.firstIndex ? 'cursor-pointer' : 'opacity-20 pointer-events')}
        onClick={decreasePages}
        aria-label='Go to the previous page'
      >
        <Icon.RightArrow 
          className='fill-primary transform rotate-180'
          title='Previous page button'
          desc='Go to the previous page' 
        />
      </Button.Common>
      <Button.Common 
        name='Go to the next page'
        className={'flex items-center rounded without-common-hover on-hover-shadow outline-auto-visible ml-1 sm:ml-2 ' + ( props.lastIndex + pageValues.indexDecrease !== trackLength ? 'cursor-pointer' : 'opacity-20 pointer-events')}
        onClick={increasePages}
        aria-label='Go to the next page'
      >
        <Icon.RightArrow 
          className='fill-primary' 
          title='Next page button'
          desc='Go to the next page' 
        />
      </Button.Common>
    </div>
  );
};

export default SessionsPagination;
